import React, { useEffect, createElement } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { getArticleBySlug, helpArticles, helpCategories } from '../data/helpArticles';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
export function HelpArticle() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const article = slug ? getArticleBySlug(slug) : undefined;
  useEffect(() => {
    if (article) {
      document.title = article.seoTitle;
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.metaDescription);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = article.metaDescription;
        document.head.appendChild(meta);
      }
    }
  }, [article]);
  if (!article) {
    return <Layout data-id="element-450">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-451">
          <div className="text-center" data-id="element-452">
            <h1 className="text-2xl font-bold mb-4" data-id="element-453">Article not found</h1>
            <p className="text-muted-foreground mb-6" data-id="element-454">
              The article you're looking for doesn't exist or has been moved.
            </p>
            <Link to="/help" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white" style={{
            backgroundColor: '#ff6b6b'
          }} data-id="element-455">
              <ArrowLeft className="w-4 h-4" data-id="element-456" />
              Back to Help Center
            </Link>
          </div>
        </div>
      </Layout>;
  }
  const category = helpCategories.find(c => c.slug === article.categorySlug);
  const relatedArticles = article.relatedArticles.map(slug => helpArticles.find(a => a.slug === slug)).filter(Boolean);
  return <Layout data-id="element-457">
      <div className="min-h-screen bg-gray-50" data-id="element-458">
        {/* Breadcrumb */}
        <div className="bg-white border-b" data-id="element-459">
          <div className="container mx-auto px-4 py-4" data-id="element-460">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-461">
              <Link to="/help" className="hover:text-gray-900 transition-colors" data-id="element-462">
                Help Center
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-463" />
              <Link to={`/help?category=${article.categorySlug}`} className="hover:text-gray-900 transition-colors" data-id="element-464">
                {article.category}
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-465" />
              <span className="text-gray-900 font-medium truncate" data-id="element-466">
                {article.title}
              </span>
            </nav>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12" data-id="element-467">
          <div className="max-w-3xl mx-auto" data-id="element-468">
            {/* Back Button */}
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors" data-id="element-469">
              <ArrowLeft className="w-4 h-4" data-id="element-470" />
              Back
            </button>

            {/* Article Header */}
            <div className="mb-8" data-id="element-471">
              <div className="flex items-center gap-2 mb-4" data-id="element-472">
                <span className="text-2xl" data-id="element-473">{category?.icon}</span>
                <span className="text-sm font-medium text-pink-500 uppercase tracking-wider" data-id="element-474">
                  {article.category}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-475">
                {article.title}
              </h1>
              <p className="text-xl text-muted-foreground" data-id="element-476">
                {article.metaDescription}
              </p>
            </div>

            {/* Article Content */}
            <article className="bg-white rounded-2xl p-8 md:p-12 border border-gray-100 shadow-sm" data-id="element-477">
              <div className="prose prose-gray max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-gray-600 prose-p:leading-relaxed prose-li:text-gray-600 prose-strong:text-gray-900 prose-a:text-pink-500 prose-a:no-underline hover:prose-a:underline prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none" data-id="element-478">
                <ReactMarkdown data-id="element-479">{article.content}</ReactMarkdown>
              </div>
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && <div className="mt-12" data-id="element-480">
                <h2 className="text-xl font-bold mb-6" data-id="element-481">Related Articles</h2>
                <div className="grid gap-4" data-id="element-482">
                  {relatedArticles.map(related => related && <Link key={related.id} to={`/help/${related.slug}`} className="bg-white p-5 rounded-xl border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all group flex items-center gap-4" data-id="element-483">
                          <div className="flex-1" data-id="element-484">
                            <h3 className="font-bold group-hover:text-pink-500 transition-colors" data-id="element-485">
                              {related.title}
                            </h3>
                            <span className="text-sm text-muted-foreground" data-id="element-486">
                              {related.category}
                            </span>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" data-id="element-487" />
                        </Link>)}
                </div>
              </div>}

            {/* Contact Support */}
            <div className="mt-12 text-center" data-id="element-488">
              <div className="bg-gray-100 rounded-2xl p-8" data-id="element-489">
                <h3 className="text-lg font-bold mb-2" data-id="element-490">Was this helpful?</h3>
                <p className="text-muted-foreground mb-4" data-id="element-491">
                  Still have questions? We're here to help.
                </p>
                <a href="mailto:help@flinglink.app" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105" style={{
                backgroundColor: '#ff6b6b'
              }} data-id="element-492">
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
}